import Immutable from "immutable";
import RecordClassCreator, { SingleResourcePayload } from "services/record";
import { trackMessage } from "services/errorTracker";

export enum Features {
  ADA_GLASS_NUANCE = "adaGlassNuance",
  ADA_GLASS_SALESFORCE = "adaGlassSalesforce",
  ADA_GLASS_ZENDESK = "adaGlassZendesk",
  ADA_MESSENGER = "adaMessenger",
  AUTHENTICATION = "authentication",
  BOT_ROUTING = "botRouting",
  CLIENT_TIMEZONE_TRACKING = "clientTimezoneTracking",
  CONDITIONALS = "conditionals",
  CSAT_PRO_BLOCK = "csatProBlock",
  CSAT_V2 = "csatV2",
  END_CHAT = "endChat",
  FORCED_QUICK_REPLIES = "forcedQuickReplies",
  LIVEPERSON_CLIENT = "livepersonClient",
  NO_BRANDING = "noBranding",
  PERSONALIZATION = "personalization",
  PREDICTIVE_SUGGESTIONS = "predictiveSuggestions",
  TRANSLATIONS = "translations",
  SALESFORCE_FILE_UPLOAD = "salesforceFileTransfer",
  SATISFACTION = "satisfaction",
  SEGMENTATION = "segmentation",
  QUEUE_BOT_BLOCK = "queueBotBlock",
  QUICK_REPLIES_BLOCK = "quickRepliesBlock",
  ZENDESK_AGENT_WORKSPACE = "zendeskAgentWorkspace",
  ZENDESK_CHATTER_AUTH = "adaGlassZendeskChatterAuth",
  ZENDESK_CLIENT = "zendeskClient"
}

export interface ClientAttributes {
  language: string;
  agentAvatar?: Immutable.Map<string, unknown>;
  avatar?: Immutable.Map<string, unknown>;
  autoTranslation?: boolean;
  bestGuesses?: unknown;
  botRouting?: boolean;
  chatButton?: Immutable.Map<string, unknown>;
  enforceMfa?: Immutable.Map<string, unknown>;
  expressionFilters?: Immutable.List<unknown>;
  features?: Immutable.Map<string, boolean>;
  handoff?: Immutable.Map<string, unknown>;
  handle?: string;
  name?: string;
  negResponse?: unknown;
  numPredictiveSuggestions?: number;
  onsched?: Immutable.Map<string, unknown>;
  posResponse?: unknown;
  redactions?: Immutable.List<unknown>;
  responses?: Immutable.Map<string, unknown>;
  routingInfo?: Immutable.Map<string, unknown>;
  scheduledBlockSchedules?: Immutable.List<unknown>;
  schedules?: Immutable.List<unknown>;
  tier?: string;
  tint?: string;
  token?: string;
  privacySettings?: Immutable.Map<string, unknown>;
  translatedLanguages?: Immutable.List<unknown>;
}

export default class Client extends RecordClassCreator<ClientAttributes>({
  type: "client",
  agentAvatar: Immutable.Map(),
  avatar: Immutable.Map(),
  autoTranslation: false,
  bestGuesses: null,
  botRouting: false,
  chatButton: Immutable.Map(),
  enforceMfa: Immutable.Map(),
  expressionFilters: Immutable.List(),
  features: Immutable.Map(),
  handoff: Immutable.Map(),
  handle: null,
  language: null,
  name: null,
  negResponse: null,
  numPredictiveSuggestions: null,
  onsched: Immutable.Map(),
  posResponse: null,
  redactions: Immutable.List(),
  responses: Immutable.Map(),
  routingInfo: Immutable.Map(),
  scheduledBlockSchedules: Immutable.List(),
  schedules: Immutable.List(),
  tier: null,
  tint: null,
  token: null,
  privacySettings: Immutable.Map(),
  translatedLanguages: Immutable.List()
}) {
  onUpdate(): this {
    // Update document title when name changes
    document.title = this.name ? `${this.name} — Ada` : "Ada";

    return this;
  }

  /**
   * Methods
   */

  hasFeature(featureName: Features): boolean {
    return Boolean(this.features.get(featureName));
  }

  /**
   * Getters
   */

  get hasFullSitePrivileges(): boolean {
    const hasFullSitePrivileges = ["pro", "custom", "private"].includes(this.tier);

    // https://adasupport.atlassian.net/browse/BUIL-307
    if (!hasFullSitePrivileges) {
      trackMessage(`Client with id: ${this.id} has ${this.tier} tier. This is likely incorrect and should be investigated. ${JSON.stringify(this.attributes.toJS())}`);
    }

    return hasFullSitePrivileges;
  }

  get hasLiveAgentFeature(): boolean {
    return [
      Features.ADA_GLASS_NUANCE,
      Features.ADA_GLASS_SALESFORCE,
      Features.ADA_GLASS_ZENDESK
    ].some(feature => this.hasFeature(feature));
  }

  get isZendeskEnabled(): boolean {
    return Boolean(this.handoff.getIn(["integrations", "zendesk", "enabled"]));
  }

  get invalidFields(): Immutable.List<string> {
    const result = [];
    const avatarImage = this.avatar.get("image") as string;
    const isBotImageSvg = Boolean(
      avatarImage && avatarImage.split(".").pop() === "svg"
    );

    if (this.avatar.get("useLetter") && !this.avatar.get("letter")) {
      result.push("botAvatarLetter");
    } else if (!this.avatar.get("useLetter") && !isBotImageSvg) {
      result.push("botAvatarImage");
    }

    if (!this.agentAvatar.get("default") && !this.agentAvatar.get("image")) {
      result.push("agentAvatarImage");
    }

    if (!this.chatButton.get("iconPath")) {
      result.push("chatButtonIconPath");
    }

    return Immutable.List(result);
  }

  get isValid(): boolean {
    return this.invalidFields.size === 0;
  }

  static getFetchOneUrl(): string {
    return "/";
  }

  static resolveFetchOneResult(payload: { client: { _id: string } }): SingleResourcePayload {
    return {
      ...payload,
      data: {
        ...payload.client,
        _type: "client",
        _id: payload.client._id || "unknown"
      }
    };
  }
}
